// Copyright (c) 2025 Nebularis Holdings LLC
// Licensed to Nutrx Solutions under a non-exclusive, non-transferable license.
// See IP License Agreement for usage restrictions.
// frontend/src/components/learning/LearningSummaryStrip.jsx

import React from "react";

function clean(value) {
  const text = String(value ?? "").trim();
  return text || "";
}

function asNumber(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function percent(value, fallback = "—") {
  const parsed = asNumber(value);
  if (parsed === null) return fallback;
  return `${Math.round(parsed)}%`;
}

function minutes(value, fallback = "—") {
  const parsed = asNumber(value);
  if (parsed === null) return fallback;
  return `${Math.round(parsed)}m`;
}

function titleCase(value, fallback = "—") {
  const raw = clean(value);
  if (!raw) return fallback;
  return raw
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function guardrailsLocked(guardrails = {}) {
  return (
    guardrails.autonomous_decision_making === false &&
    guardrails.priority_mutation === false &&
    guardrails.recommendation_mutation === false &&
    guardrails.workflow_execution === false &&
    guardrails.approval_execution === false &&
    guardrails.sop_rewrite === false &&
    guardrails.cloud_resource_mutation === false
  );
}

function firstArray(...values) {
  for (const value of values) {
    if (Array.isArray(value) && value.length) return value;
  }
  return [];
}

function LearningSummaryMetric({ label, value, footnote, tone = "neutral" }) {
  return (
    <div className={`learning-summary-metric learning-summary-metric-${tone}`}>
      <span>{label}</span>
      <strong>{value ?? "—"}</strong>
      {footnote ? <small>{footnote}</small> : null}
    </div>
  );
}

export default function LearningSummaryStrip({
  context = null,
  loading = false,
  error = "",
  refreshState = "idle",
  onRefresh,
}) {
  const learning = context && typeof context === "object" ? context : null;
  const summary = learning?.summary || {};
  const effectiveness = learning?.effectiveness || {};
  const recommendationQuality =
    summary?.recommendation_quality || effectiveness?.recommendation_quality || {};
  const executionQuality =
    summary?.execution_plan_quality || effectiveness?.execution_plan_quality || {};
  const validationLearning = learning?.validation_learning || effectiveness?.validation_learning || {};
  const optimization = learning?.continuous_optimization || {};
  const operatorEfficiency = optimization?.operator_efficiency || {};
  const blockers = firstArray(
    summary?.common_blockers,
    learning?.common_blockers,
    validationLearning?.risk_conditions
  );
  const locked = guardrailsLocked(learning?.guardrails || {});
  const effectiveRefreshState = refreshState || (loading && learning ? "refreshing" : "idle");
  const isRefreshing = effectiveRefreshState === "refreshing";
  const isRefreshSuccess = effectiveRefreshState === "success";
  const isRefreshError = effectiveRefreshState === "error";
  const refreshButtonLabel = isRefreshing
    ? "Refreshing…"
    : isRefreshSuccess
      ? "Refreshed"
      : isRefreshError
        ? "Retry Refresh"
        : "Refresh Learning";
  const refreshMessage = isRefreshing
    ? "Refreshing organizational learning summary…"
    : isRefreshSuccess
      ? "Learning summary refreshed."
      : isRefreshError
        ? "Learning summary refresh failed. Existing summary is still shown."
        : "";

  if (loading && !learning) {
    return (
      <section className="learning-summary-strip learning-summary-strip-loading" aria-label="Learning Summary">
        <div className="learning-summary-header">
          <div>
            <span>Learning Summary</span>
            <h3>Loading organizational learning…</h3>
            <p>OASYS is reading outcome memory, recommendation effectiveness, and validation learning.</p>
          </div>
        </div>
      </section>
    );
  }

  if (error && !learning) {
    return (
      <section className="learning-summary-strip learning-summary-strip-error" aria-label="Learning Summary">
        <div className="learning-summary-header">
          <div>
            <span>Learning Summary</span>
            <h3>Learning summary unavailable</h3>
            <p>{error}</p>
          </div>
          {onRefresh ? (
            <button type="button" className="mec-link-button" onClick={onRefresh} disabled={isRefreshing}>
              {isRefreshing ? "Refreshing…" : "Retry"}
            </button>
          ) : null}
        </div>
      </section>
    );
  }

  return (
    <section
      className={`learning-summary-strip${isRefreshing ? " learning-summary-strip-refreshing" : ""}`}
      aria-label="Learning Summary"
      aria-busy={isRefreshing}
    >
      <div className="learning-summary-header">
        <div>
          <span>Learning Summary</span>
          <h3>OASYS remembers what worked.</h3>
          <p>
            Outcome memory now informs guidance, execution planning confidence, validation readiness,
            and operator optimization. Learning remains advisory only.
          </p>
        </div>
        <div className="learning-summary-actions">
          <span className={locked ? "learning-summary-lock" : "learning-summary-lock learning-summary-lock-review"}>
            {locked ? "Guardrails locked" : "Review guardrails"}
          </span>
          {onRefresh ? (
            <button
              type="button"
              className="mec-link-button"
              onClick={onRefresh}
              disabled={isRefreshing}
            >
              {refreshButtonLabel}
            </button>
          ) : null}
          {refreshMessage ? (
            <span className={`learning-summary-status learning-summary-status-${effectiveRefreshState}`} role="status">
              {refreshMessage}
            </span>
          ) : null}
        </div>
      </div>

      <div className="learning-summary-grid">
        <LearningSummaryMetric
          label="Recommendation Quality"
          value={percent(recommendationQuality?.accepted_rate)}
          footnote={`${percent(recommendationQuality?.validated_rate)} validated`}
          tone="good"
        />
        <LearningSummaryMetric
          label="Validation Success"
          value={percent(summary?.validation_success_rate || validationLearning?.validation_success_rate)}
          footnote="Learning signal"
          tone="good"
        />
        <LearningSummaryMetric
          label="Average Completion"
          value={minutes(summary?.average_completion_minutes)}
          footnote={clean(operatorEfficiency?.average_completion_improvement) || "Improving"}
          tone="neutral"
        />
        <LearningSummaryMetric
          label="Plan Accuracy"
          value={percent(executionQuality?.effort_estimate_accuracy_rate)}
          footnote={`${percent(executionQuality?.rollback_rate)} rollback rate`}
          tone="neutral"
        />
        <LearningSummaryMetric
          label="Learning Coverage"
          value={summary?.previous_outcomes_analyzed ?? "—"}
          footnote={titleCase(summary?.learning_coverage, "Outcome samples")}
          tone="neutral"
        />
      </div>

      <div className="learning-summary-footer">
        <div>
          <span>Common blockers</span>
          <p>{blockers.length ? blockers.slice(0, 3).map(titleCase).join(" · ") : "No common blockers identified yet."}</p>
        </div>
        <div>
          <span>Continuous optimization</span>
          <p>{titleCase(optimization?.status || summary?.learning_status || "active")} · {titleCase(optimization?.mode || summary?.learning_mode || "advisory only")}</p>
        </div>
      </div>
    </section>
  );
}
